/**
 * ChatLog — scroll container for a conversation of `<MessageRow>` children.
 *
 * Owns the right CSS for an append-only chat surface: vertical overflow, a
 * `scrollbar-gutter: stable` reserve so the column doesn't jiggle when chips
 * appear, and `overflow-anchor: auto` so newly-rendered messages don't yank
 * the scroll position when the user is reading older content. Wraps children
 * in a flex column with consistent vertical rhythm.
 *
 * Composition:
 *
 *   const { scrollRef, sentinelRef, isAtBottom, scrollToBottom } =
 *     useScrollToBottom<HTMLDivElement, HTMLDivElement>();
 *
 *   <ChatLog
 *     ref={scrollRef}
 *     onLoadOlder={loadOlder}
 *     isLoadingOlder={isLoadingOlder}
 *     empty={<EmptyState title="Say hi to AIKO" />}
 *   >
 *     {messages.map(m => <MessageRow key={m.id} {...m} />)}
 *     <div ref={sentinelRef} aria-hidden />
 *   </ChatLog>
 *   <ScrollToBottomButton visible={!isAtBottom} onClick={() => scrollToBottom()} />
 *
 * Pagination: pass `onLoadOlder` to render a 1px sentinel at the top of the
 * log; when the user scrolls it into view the callback fires once. Pass
 * `isLoadingOlder` to surface a small `<Spinner>` above the messages while
 * the older page is loading.
 *
 * Empty state: pass an `empty` slot to render when there are no children.
 *
 * Virtualization: this primitive does NOT implement windowing. Its DOM shape
 * — a single overflow scroller with a flat children stack — is friendly to
 * `react-virtuoso` / `@tanstack/react-virtual` should that become the
 * consumer's need. Wrapping in a virtualizer is a one-line swap from the
 * consumer's side: replace the children render with the virtualizer's
 * ScrollerProvider; ChatLog stays the same shell.
 */
import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";
import { Spinner } from "../primitives/Spinner";
import { Skeleton } from "../primitives/Skeleton";

/* ============================================================================
 * Variants
 * ========================================================================= */

const chatLogVariants = cva(
  cn(
    "relative flex flex-col gap-3 overflow-y-auto",
    // Reserve scrollbar space so chip layouts on the right don't jiggle when
    // the bar appears or disappears.
    "[scrollbar-gutter:stable]",
    // Newly-rendered messages don't yank the user's reading position.
    "[overflow-anchor:auto]",
  ),
  {
    variants: {
      density: {
        comfortable: "px-4 py-4",
        compact: "px-3 py-3",
      },
    },
    defaultVariants: { density: "comfortable" },
  },
);

/* ============================================================================
 * Component
 * ========================================================================= */

export interface ChatLogProps
  extends
    Omit<React.HTMLAttributes<HTMLDivElement>, "children">,
    VariantProps<typeof chatLogVariants> {
  /** Conversation rows. Conventionally `<MessageRow>` children. */
  children?: React.ReactNode;
  /**
   * Empty-state slot. Rendered ONLY when `children` is falsy / empty
   * (see `isEmpty` heuristic below). Use to surface "Say hi to AIKO" before
   * the first message lands.
   */
  empty?: React.ReactNode;
  /**
   * Pagination hook — fires once when the top sentinel intersects the
   * scroller. Wire to "fetch older messages". Skipped entirely when omitted.
   */
  onLoadOlder?: () => void;
  /**
   * When true, renders a small AIKO-voiced "Loading earlier messages…"
   * affordance at the top of the log. Pair with `onLoadOlder`.
   */
  isLoadingOlder?: boolean;
  /** Override the loading-older copy. Default `"Loading earlier messages"`. */
  loadingOlderLabel?: string;
  /**
   * The transcript itself has not arrived yet — renders alternating message
   * silhouettes in place of the rows.
   *
   * This is a different fact from `isLoadingOlder`, which means "the rows you
   * are reading are real, an OLDER page is on its way" and therefore belongs
   * above them rather than instead of them. Both can be true only in the
   * degenerate case of a first load, where this one wins.
   *
   * It also outranks `empty`: a conversation that has not loaded is not a
   * conversation with no messages, and "Say hi to AIKO" flashed over a
   * transcript that is about to appear is a lie the user has to un-read.
   */
  loading?: boolean;
  /** Silhouette count while `loading`. Default 3. */
  loadingRows?: number;
  /** Accessible name for the loading region. Default `"Loading conversation"`. */
  loadingLabel?: string;
}

/**
 * Detect "no children" — covers `null`, `undefined`, `false`, an empty array,
 * and an empty fragment. A single child or any non-empty array counts as
 * non-empty.
 */
function isChildrenEmpty(children: React.ReactNode): boolean {
  if (children == null || children === false) return true;
  if (Array.isArray(children)) return children.length === 0;
  return false;
}

export const ChatLog = React.forwardRef<HTMLDivElement, ChatLogProps>(
  (
    {
      className,
      children,
      empty,
      onLoadOlder,
      isLoadingOlder = false,
      loadingOlderLabel = "Loading earlier messages",
      loading = false,
      loadingRows = 3,
      loadingLabel = "Loading conversation",
      density,
      ...rest
    },
    ref,
  ) => {
    const empty_ = isChildrenEmpty(children);

    // A transcript that has not loaded has nothing to paginate and nothing to
    // call empty, so this branch owns the whole scroller.
    if (loading) {
      return (
        <div
          ref={ref}
          className={cn(chatLogVariants({ density }), className)}
          role="status"
          aria-busy="true"
          aria-label={loadingLabel}
          {...rest}
        >
          {Array.from({ length: Math.max(1, loadingRows) }).map((_, i) => (
            <MessageRowSkeleton key={i} side={i % 2 === 0 ? "assistant" : "user"} />
          ))}
        </div>
      );
    }

    return (
      <div ref={ref} className={cn(chatLogVariants({ density }), className)} {...rest}>
        {onLoadOlder ? <TopSentinel onIntersect={onLoadOlder} disabled={isLoadingOlder} /> : null}

        {isLoadingOlder ? (
          <div
            role="status"
            aria-live="polite"
            className="flex items-center justify-center gap-2 py-2 text-xs text-muted-foreground"
          >
            <Spinner size="sm" decorative />
            <span>{loadingOlderLabel}</span>
          </div>
        ) : null}

        {empty_ && empty ? <>{empty}</> : children}
      </div>
    );
  },
);
ChatLog.displayName = "ChatLog";

/* ============================================================================
 * Internal — one message silhouette
 *
 * Alternates sides because a transcript does. A single-column stack of equal
 * grey bars reads as a list, and the eye has to re-learn the shape when the
 * real rows land; alternating means the skeleton and the conversation share a
 * silhouette. Assistant rows carry an avatar and run wider — the same
 * asymmetry `MessageRow` renders.
 * ========================================================================= */

function MessageRowSkeleton({ side }: { side: "user" | "assistant" }) {
  const assistant = side === "assistant";
  return (
    <div
      aria-hidden="true"
      className={cn("flex w-full gap-2", assistant ? "justify-start" : "justify-end")}
    >
      {assistant ? <Skeleton variant="avatar" className="h-7 w-7 shrink-0" /> : null}
      <div className={cn("space-y-2", assistant ? "w-[68%]" : "w-[42%]")}>
        <Skeleton variant="text" animation="shimmer" className="h-3.5 w-full" />
        <Skeleton
          variant="text"
          animation="shimmer"
          className={cn("h-3.5", assistant ? "w-[82%]" : "w-[64%]")}
        />
        {assistant ? (
          <Skeleton variant="text" animation="shimmer" className="h-3.5 w-[45%]" />
        ) : null}
      </div>
    </div>
  );
}

/* ============================================================================
 * Internal — top sentinel
 *
 * 1px-tall element at the top of the scroller. When it intersects the
 * scroller's root, fires `onIntersect` once. The `disabled` prop suppresses
 * firing while the consumer is mid-load (`isLoadingOlder`) so the page-load
 * doesn't fire repeatedly.
 *
 * Lives inline rather than as a public hook because the wiring is specific
 * to ChatLog's internal structure (top-of-list intersection + load-once
 * latch); pulling it out would over-generalize for one consumer.
 * ========================================================================= */

interface TopSentinelProps {
  onIntersect: () => void;
  disabled: boolean;
}

function TopSentinel({ onIntersect, disabled }: TopSentinelProps) {
  const ref = React.useRef<HTMLDivElement>(null);
  const onIntersectRef = React.useRef(onIntersect);
  const disabledRef = React.useRef(disabled);

  React.useEffect(() => {
    onIntersectRef.current = onIntersect;
    disabledRef.current = disabled;
  }, [onIntersect, disabled]);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") return;

    let lastFiredAt = 0;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        if (disabledRef.current) return;
        // Coarse rate-limit so a momentarily-visible sentinel during a layout
        // shift doesn't fire onIntersect twice in rapid succession.
        const now = Date.now();
        if (now - lastFiredAt < 250) return;
        lastFiredAt = now;
        onIntersectRef.current();
      },
      // Root defaults to the nearest ancestor scroll container, which is
      // ChatLog itself.
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return <div ref={ref} aria-hidden className="h-px shrink-0" />;
}
